<script setup lang="ts">
import { ref, computed, toRef } from "vue";
import { trackAppFeature } from "@/analytics/app-analytics";
import { useTranslation } from "i18next-vue";
import type { FieldObservation } from "@/types/field-observations.types";
import { useFieldObservationForm } from "@/composable/useFieldObservationForm";
import FieldInput from "./FieldInput.vue";
import FieldHeader from "./FieldHeader.vue";
import CameraOverlay from "./CameraOverlay.vue";
import VoiceRecordingOverlay from "./VoiceRecordingOverlay.vue";
import PartSelector from "./PartSelector.vue";
import MobileConfirmModal from "./MobileConfirmModal.vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import { usePwaController } from "@/composable/usePWA";

const props = defineProps<{
  diagnosticId: number;
  observation?: FieldObservation; // For edit mode
  initialImages?: File[]; // For creating observation with photos from list
  initialAudioBlob?: Blob; // For creating observation with audio from list
}>();

const emit = defineEmits<{
  (e: "close"): void;
}>();

const { t } = useTranslation();
const { isMobileOrTablet } = usePwaController();

// Toute la logique du formulaire (création/édition, sauvegarde live, médias)
// est partagée avec l'éditeur compact du canvas via ce composable.
const {
  isInitializing,
  selectedType,
  selectedPartId,
  description,
  existingPhotos,
  existingAudioRecordings,
  isUploadingPhoto,
  isUploadingAudio,
  showDeleteConfirm,
  isDeleting,
  showDeleteObservationConfirm,
  isDeletingObservation,
  uploadPhotos,
  uploadAudioBlob,
  confirmDeletePhoto,
  confirmDeleteAudio,
  handleDelete,
  handleDeleteObservation,
} = useFieldObservationForm({
  onCreated: () => trackAppFeature("field_observation_created"),
  diagnosticId: toRef(props, "diagnosticId"),
  observation: props.observation,
  initialImages: props.initialImages,
  initialAudioBlob: props.initialAudioBlob,
  onClose: () => emit("close"),
});

const headerTitle = computed(() => {
  if (props.observation) {
    return t("dashboard:fieldObservations.edit");
  }
  return t("dashboard:fieldObservations.newObservation");
});

// Photo handling (device-specific: camera overlay on mobile, file picker on desktop)
const photoInput = ref<HTMLInputElement | null>(null);
const showCameraOverlay = ref(false);

const openPhotoPicker = () => {
  if (isMobileOrTablet()) {
    showCameraOverlay.value = true;
  } else {
    photoInput.value?.click();
  }
};

const handleCameraClose = () => {
  showCameraOverlay.value = false;
};

const handleCameraCaptured = async (files: File[]) => {
  showCameraOverlay.value = false;
  await uploadPhotos(files);
};

const handlePhotoChange = async (event: Event) => {
  const input = event.target as HTMLInputElement;
  const files = input.files;
  if (files && files.length > 0) {
    await uploadPhotos(Array.from(files));
  }
  input.value = "";
};

// Audio handling
const showVoiceOverlay = ref(false);

const openVoiceRecording = () => {
  showVoiceOverlay.value = true;
};

const handleVoiceClose = () => {
  showVoiceOverlay.value = false;
};

const handleVoiceRecorded = async (audioBlob: Blob) => {
  showVoiceOverlay.value = false;
  await uploadAudioBlob(audioBlob);
};

// Close handler
const handleClose = () => {
  emit("close");
};
</script>

<template>
  <div class="observation-editor">
    <div class="editor-wrapper">
      <!-- Header -->
      <FieldHeader :title="headerTitle" @back="handleClose" />

      <!-- Loading state -->
      <div v-if="isInitializing" class="loading-state">
        <span class="spinner-border" />
        <p>{{ t("dashboard:fieldObservations.creating") }}</p>
      </div>

      <template v-else>
        <div class="editor-content">
          <div class="editor-content-inner">
            <!-- Type selector -->
            <div class="type-selector-section">
              <div class="type-btn-group">
                <button
                  class="type-btn"
                  :class="{ selected: selectedType === 'DISORDER' }"
                  @click="selectedType = 'DISORDER'"
                >
                  <PhosphorIcon name="warning" :size="18" weight="bold" />
                  <span>{{
                    t("dashboard:fieldObservations.type.DISORDER")
                  }}</span>
                </button>
                <button
                  class="type-btn"
                  :class="{ selected: selectedType === 'OVERVIEW' }"
                  @click="selectedType = 'OVERVIEW'"
                >
                  <PhosphorIcon name="camera" :size="18" weight="bold" />
                  <span>{{
                    t("dashboard:fieldObservations.type.OVERVIEW")
                  }}</span>
                </button>
                <button
                  class="type-btn"
                  :class="{ selected: selectedType === 'COMMENT' }"
                  @click="selectedType = 'COMMENT'"
                >
                  <PhosphorIcon name="chat-text" :size="18" weight="bold" />
                  <span>{{
                    t("dashboard:fieldObservations.type.COMMENT")
                  }}</span>
                </button>
              </div>
            </div>

            <!-- Section: Photos -->
            <div class="editor-section">
              <div class="section-header">
                <PhosphorIcon name="camera" :size="20" />
                <span>{{
                  t("dashboard:fieldObservations.sections.photos")
                }}</span>
                <span
                  v-if="isUploadingPhoto"
                  class="spinner-border spinner-border-sm ms-2"
                />
              </div>
              <div class="section-content">
                <div class="media-grid">
                  <div
                    v-for="photo in existingPhotos"
                    :key="photo.id"
                    class="media-item"
                  >
                    <div v-if="photo.isLoading" class="media-loading">
                      <span class="spinner-border spinner-border-sm" />
                    </div>
                    <img v-else :src="photo.url" alt="Photo" />
                    <button
                      class="btn-remove-media"
                      @click="confirmDeletePhoto(photo.id)"
                    >
                      <PhosphorIcon name="trash" :size="14" weight="bold" />
                    </button>
                  </div>
                  <button class="add-media-btn" @click="openPhotoPicker">
                    <PhosphorIcon name="plus" :size="24" />
                  </button>
                </div>
              </div>
            </div>

            <!-- Hidden photo input -->
            <input
              ref="photoInput"
              type="file"
              accept="image/*"
              multiple
              class="d-none"
              @change="handlePhotoChange"
            />

            <!-- Section: Vocaux -->
            <div class="editor-section">
              <div class="section-header">
                <PhosphorIcon name="microphone" :size="20" />
                <span>{{
                  t("dashboard:fieldObservations.sections.audio")
                }}</span>
                <span
                  v-if="isUploadingAudio"
                  class="spinner-border spinner-border-sm ms-2"
                />
              </div>
              <div class="section-content">
                <div
                  v-if="existingAudioRecordings.length > 0"
                  class="audio-list"
                >
                  <div
                    v-for="audio in existingAudioRecordings"
                    :key="audio.id"
                    class="audio-item"
                  >
                    <div v-if="audio.isLoading" class="audio-loading">
                      <span class="spinner-border spinner-border-sm" />
                    </div>
                    <audio
                      v-else
                      :src="audio.url"
                      class="audio-player"
                      controls
                    />
                    <button
                      class="btn-remove-media"
                      @click="confirmDeleteAudio(audio.id)"
                    >
                      <PhosphorIcon name="trash" :size="14" weight="bold" />
                    </button>
                  </div>
                </div>
                <button class="btn-add-audio" @click="openVoiceRecording">
                  <PhosphorIcon name="plus" :size="20" />
                </button>
              </div>
            </div>

            <!-- Section: Notes -->
            <div class="editor-section">
              <div class="section-header">
                <PhosphorIcon name="pencil-simple" :size="20" />
                <span>{{
                  t("dashboard:fieldObservations.sections.notes")
                }}</span>
              </div>
              <div class="section-content">
                <FieldInput
                  v-model="description"
                  :placeholder="
                    t('dashboard:fieldObservations.descriptionPlaceholder')
                  "
                  :min-rows="2"
                />
              </div>
            </div>

            <!-- Section: Partie -->
            <div class="editor-section">
              <div class="section-header">
                <PhosphorIcon name="tree-structure" :size="20" />
                <span>{{ t("dashboard:fieldObservations.part.label") }}</span>
              </div>
              <div class="section-content">
                <PartSelector
                  v-model="selectedPartId"
                  :diagnostic-id="diagnosticId"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Sticky footer -->
        <div class="editor-footer">
          <div class="editor-footer-inner">
            <button
              class="btn-delete-observation"
              @click="showDeleteObservationConfirm = true"
            >
              {{ t("delete") }}
            </button>
            <button class="btn-done" @click="handleClose">
              {{ t("done") }}
            </button>
          </div>
        </div>
      </template>
    </div>

    <!-- Camera overlay -->
    <CameraOverlay
      v-if="showCameraOverlay"
      @close="handleCameraClose"
      @captured="handleCameraCaptured"
    />

    <!-- Voice recording overlay -->
    <VoiceRecordingOverlay
      v-if="showVoiceOverlay"
      :diagnostic-id="diagnosticId"
      @close="handleVoiceClose"
      @recorded="handleVoiceRecorded"
    />

    <!-- Delete media confirmation modal (mobile-friendly) -->
    <MobileConfirmModal
      v-model="showDeleteConfirm"
      :title="t('dashboard:fieldObservations.confirmDeleteMedia.title')"
      :message="t('dashboard:fieldObservations.confirmDeleteMedia.message')"
      :confirm-label="t('delete')"
      :loading="isDeleting"
      danger
      @confirm="handleDelete"
    />

    <!-- Delete observation confirmation modal (mobile-friendly) -->
    <MobileConfirmModal
      v-model="showDeleteObservationConfirm"
      :title="t('dashboard:fieldObservations.confirmDelete.title')"
      :message="t('dashboard:fieldObservations.confirmDelete.message')"
      :confirm-label="t('delete')"
      :loading="isDeletingObservation"
      danger
      @confirm="handleDeleteObservation"
    />
  </div>
</template>

<style scoped lang="scss">
@import "@/style/variables.scss";

.observation-editor {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background-color: var(--bs-white);
  display: flex;
  flex-direction: column;
}

.editor-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.loading-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  color: var(--bs-secondary-color);

  .spinner-border {
    width: 40px;
    height: 40px;
  }

  p {
    font-size: 16px;
    margin: 0;
  }
}

// Type selector button group
.type-selector-section {
  padding: 16px;
  background-color: var(--bs-white);
}

.type-btn-group {
  display: flex;
  gap: 8px;
  background-color: var(--bs-tertiary-bg);
  border-radius: calc(var(--bs-border-radius) * 2);
  padding: 4px;
}

.type-btn {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 12px 8px;
  border: none;
  border-radius: calc(var(--bs-border-radius) * 1.5);
  background-color: transparent;
  color: var(--bs-secondary-color);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(.selected) {
    background-color: var(--bs-secondary-bg);
  }

  &.selected {
    background-color: var(--bs-white);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  // DISORDER - red
  &:nth-child(1).selected {
    background-color: #ef4444;
    color: white;
  }

  // OVERVIEW - blue
  &:nth-child(2).selected {
    background-color: #3b82f6;
    color: white;
  }

  // COMMENT - purple
  &:nth-child(3).selected {
    background-color: #8b5cf6;
    color: white;
  }
}

.editor-content {
  flex: 1;
  overflow-y: auto;
  min-height: 0; // Important for flex scroll
  padding-bottom: 100px; // Space for sticky footer
}

// Sticky footer
.editor-footer {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: var(--bs-white);
  border-top: 1px solid var(--bs-border-color);
  padding: 16px;
  padding-bottom: max(16px, env(safe-area-inset-bottom));
}

.editor-footer-inner {
  max-width: 800px;
  margin: 0 auto;
  display: flex;
  gap: 12px;
}

.btn-delete-observation {
  flex: 1;
  padding: 14px 20px;
  border: none;
  border-radius: calc(var(--bs-border-radius) * 2);
  background-color: #ef4444;
  color: white;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background-color: #dc2626;
  }
}

.btn-done {
  flex: 1;
  padding: 14px 24px;
  border: none;
  border-radius: calc(var(--bs-border-radius) * 2);
  background-color: var(--bs-primary);
  color: white;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
}

.editor-content-inner {
  max-width: 800px;
  margin: 0 auto;
}

.editor-section {
  background-color: var(--el-bg-color);
}

.section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px 0px 16px;
  font-weight: 600;
  font-size: 14px;
  background-color: var(--bs-white);

  color: var(--bs-body-color);

  i {
    font-size: 20px;
    opacity: 0.7;
  }
}

.section-content {
  padding: 16px;
}

.media-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.media-item {
  position: relative;
  aspect-ratio: 1;
  border-radius: calc(var(--bs-border-radius) * 1.5);
  overflow: hidden;
  background-color: var(--bs-secondary-bg);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .media-loading {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--bs-secondary-color);
  }
}

.btn-remove-media {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 50%;
  background-color: #ef4444;
  color: white;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: #dc2626;
  }
}

.add-media-btn {
  aspect-ratio: 1;
  border: 2px dashed var(--bs-border-color);
  border-radius: calc(var(--bs-border-radius) * 1.5);
  background-color: transparent;
  color: var(--bs-secondary-color);
  font-size: 24px;
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    border-color: var(--bs-primary);
    color: var(--bs-primary);
  }
}

.audio-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
}

.audio-item {
  display: flex;
  align-items: center;
  gap: 10px;
  border-radius: 12px;

  .audio-loading {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 32px;
    color: var(--bs-secondary-color);
  }

  .audio-player {
    flex: 1;
    height: 32px;
    min-width: 0;
  }

  .btn-remove-media {
    position: relative;
    top: auto;
    right: auto;
  }
}

.btn-add-audio {
  width: 100%;
  height: 44px;
  border: 2px dashed var(--bs-border-color);
  border-radius: calc(var(--bs-border-radius) * 1.5);
  background-color: transparent;
  color: var(--bs-secondary-color);
  font-size: 20px;
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    border-color: var(--bs-primary);
    color: var(--bs-primary);
  }
}
</style>
